import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { api, picUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useWS } from "@/context/WSContext";
import { Send, ArrowLeft, Loader2, Coins, Sparkles, Paperclip, X, ImageIcon, Film, Mic, Play, Pause, Trash2 } from "lucide-react";
import { toast } from "sonner";
import GiftQueue, { useGiftQueue } from "@/components/GiftQueue";
import { TIERS, tierForCoins } from "@/components/GiftAnimation";

// Same preset row as comment-gifts so the UX feels identical.
const COIN_PRESETS = [10, 50, 100, 200, 300, 500, 1000];
const DEFAULT_CREATOR_PCT = 60;

// Fixed set of quick reactions — matches backend allow-list. Order = display
// order in the popover.
const REACTIONS = ["👍", "🔥", "💯", "😂", "❤️"];

// Client-side caps mirror the backend limits so users see friendly errors
// BEFORE burning bandwidth on a doomed upload.
const IMAGE_MAX_BYTES = 15 * 1024 * 1024;
const VIDEO_MAX_BYTES = 60 * 1024 * 1024;
const AUDIO_MAX_BYTES = 10 * 1024 * 1024;
// Voice notes: hard cap at 5 minutes to keep files tiny and DMs snappy.
// MediaRecorder auto-stops when the timer hits this value.
const AUDIO_MAX_SECONDS = 300;

const BACKEND = process.env.REACT_APP_BACKEND_URL || "";
const mediaUrl = (m) => {
  if (!m?.media_url) return null;
  return /^https?:\/\//.test(m.media_url) ? m.media_url : `${BACKEND}${m.media_url}`;
};

function timeLabel(iso) {
  const d = new Date(iso);
  const now = new Date();
  if (d.toDateString() === now.toDateString()) {
    return d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  }
  return d.toLocaleString([], {
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

// mm:ss for a number of seconds (float ok). Used by both the recording-in-
// progress timer and the received-voice-note duration pill.
function formatDurationSec(sec) {
  const s = Math.max(0, Math.floor(sec || 0));
  const m = Math.floor(s / 60);
  const rem = s % 60;
  return `${m}:${rem.toString().padStart(2, "0")}`;
}

// Number of bars in the visualization. 40 is enough resolution for a 5-min
// clip while staying under the 128-cap we enforce on the backend.
const WAVEFORM_BARS = 40;

// Decode a recorded audio Blob to PCM, then downsample to `WAVEFORM_BARS`
// peak-normalized floats in [0..1]. Returns [] on any failure — the UI
// gracefully renders a flat bar row in that case. All work runs offline
// (single-shot, no realtime AudioContext) so a failed decode never blocks
// the send flow.
async function computeWaveformFromBlob(blob) {
  try {
    const Ctx = window.AudioContext || window.webkitAudioContext;
    if (!Ctx || !blob) return [];
    const buf = await blob.arrayBuffer();
    // Some Safari builds only accept the callback form of decodeAudioData.
    const ctx = new Ctx();
    const decoded = await new Promise((resolve, reject) => {
      try {
        const p = ctx.decodeAudioData(buf.slice(0), resolve, reject);
        if (p && typeof p.then === "function") p.then(resolve, reject);
      } catch (e) {
        reject(e);
      }
    });
    const data = decoded.getChannelData(0);
    const total = data.length;
    if (!total) return [];
    const bucket = Math.max(1, Math.floor(total / WAVEFORM_BARS));
    const bars = new Array(WAVEFORM_BARS).fill(0);
    for (let i = 0; i < WAVEFORM_BARS; i++) {
      let peak = 0;
      const start = i * bucket;
      const end = Math.min(total, start + bucket);
      for (let j = start; j < end; j++) {
        const v = Math.abs(data[j]);
        if (v > peak) peak = v;
      }
      bars[i] = peak;
    }
    // Normalize to the loudest bar so quiet recordings still show shape.
    const max = bars.reduce((a, b) => Math.max(a, b), 0);
    if (max <= 0) return bars;
    return bars.map((b) => Math.min(1, b / max));
  } catch (e) {
    console.warn("waveform compute failed", e);
    return [];
  }
}

export default function Conversation() {
  const { conversationId } = useParams();
  const { user } = useAuth();
  const { subscribe } = useWS();
  const navigate = useNavigate();
  const [conv, setConv] = useState(null);
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const [loading, setLoading] = useState(true);
  const [coinAmount, setCoinAmount] = useState(0);
  const [showPicker, setShowPicker] = useState(false);
  const [customAmount, setCustomAmount] = useState("");
  const [creatorPct, setCreatorPct] = useState(DEFAULT_CREATOR_PCT);
  // Local media staging — when user picks a file we hold it here until they
  // confirm with Send. `uploadPct` drives the small progress bar.
  const [mediaFile, setMediaFile] = useState(null);
  const [mediaPreview, setMediaPreview] = useState(null);
  const [uploadPct, setUploadPct] = useState(0);
  // Voice-note recording state. `recording` gates the recording overlay.
  // `recordingSec` drives the live timer. Refs hold the MediaRecorder and
  // chunk buffer so React re-renders don't interrupt an in-flight stream.
  const [recording, setRecording] = useState(false);
  const [recordingSec, setRecordingSec] = useState(0);
  // Live bars sampled from the mic AnalyserNode during recording. Kept
  // separate from the final `audio_waveform` (peak-normalized PCM over the
  // full blob) — this one is instantaneous frequency data so it dances
  // while the user speaks, WhatsApp/Voxer style.
  const [liveBars, setLiveBars] = useState(() => new Array(WAVEFORM_BARS).fill(0));
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const audioStreamRef = useRef(null);
  const recordTimerRef = useRef(null);
  const recordStartAtRef = useRef(0);
  // Live-visualization refs — hold the AudioContext + AnalyserNode + rAF
  // handle for the recording strip's dancing bars. Everything is torn
  // down together in _teardownRecorder so we never leak an audio graph.
  const liveAudioCtxRef = useRef(null);
  const liveAnalyserRef = useRef(null);
  const liveRafRef = useRef(null);
  // Typing indicator (peer's state). Auto-clears after 5s to guard against a
  // stale `typing=true` event that never got its matching false counterpart.
  const [peerTyping, setPeerTyping] = useState(false);
  const scrollRef = useRef(null);
  const fileInputRef = useRef(null);
  // Refs for the outgoing typing debounce.
  const typingTimerRef = useRef(null);
  const typingLastSentRef = useRef(0);
  const peerTypingTimerRef = useRef(null);

  // Animation queue — plays for the recipient when a gift lands, and for the
  // sender as instant feedback when they hit send.
  const { queue, push, clear } = useGiftQueue();

  const loadMessages = async () => {
    try {
      const r = await api.get(
        `/messages/conversations/${conversationId}/messages`,
      );
      setMessages(r.data);
    } catch (e) {
      console.warn("loadMessages failed", e);
    }
  };

  useEffect(() => {
    if (!user) return;
    const init = async () => {
      try {
        const [cr, sr] = await Promise.all([
          api.get(`/messages/conversations/${conversationId}`),
          api.get(`/settings/gifts`).catch(() => ({ data: null })),
        ]);
        setConv(cr.data);
        if (sr.data?.creator_pct) setCreatorPct(sr.data.creator_pct);
        await loadMessages();
      } catch {
        toast.error("Conversation not found");
        navigate("/messages");
      } finally {
        setLoading(false);
      }
    };
    init();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [conversationId, user]);

  // Best-effort cleanup so we never leak an open mic if the user navigates
  // away mid-recording. Hooks-rules: MUST live above the early returns.
  useEffect(() => {
    return () => {
      if (recordTimerRef.current) clearInterval(recordTimerRef.current);
      if (liveRafRef.current) cancelAnimationFrame(liveRafRef.current);
      if (liveAudioCtxRef.current) {
        try {
          liveAudioCtxRef.current.close();
        } catch {
          /* already closed */
        }
      }
      try {
        if (
          mediaRecorderRef.current &&
          mediaRecorderRef.current.state !== "inactive"
        ) {
          mediaRecorderRef.current.stop();
        }
      } catch {
        /* already stopped */
      }
      if (audioStreamRef.current) {
        try {
          audioStreamRef.current.getTracks().forEach((t) => t.stop());
        } catch {
          /* stream already released */
        }
      }
    };
  }, []);

  // Real-time: append incoming messages + enqueue gift animation for the
  // recipient side (sender already gets an optimistic animation on submit).
  useEffect(() => {
    if (!user) return;
    return subscribe((evt) => {
      if (evt.conversation_id !== conversationId) return;

      if (evt.type === "message:new") {
        setMessages((prev) => {
          if (prev.some((m) => m.id === evt.message.id)) return prev;
          return [...prev, evt.message];
        });
        // If the incoming message is a gift TO the current user, queue it.
        const m = evt.message;
        if (m.sender_id !== user.user_id && m.coins_amount > 0) {
          push({
            id: m.id,
            coins_amount: m.coins_amount,
            gift_tier: m.gift_tier || tierForCoins(m.coins_amount),
            text: m.text,
            tipper: conv?.peer || { name: "someone", username: "someone" },
          });
        }
        if (m.sender_id !== user.user_id) {
          api
            .get(`/messages/conversations/${conversationId}/messages`)
            .catch(() => {});
        }
      }

      if (evt.type === "message:reaction") {
        // Merge the new reactions blob into the affected message so both
        // participants stay in sync without a full-list refetch.
        setMessages((prev) =>
          prev.map((m) =>
            m.id === evt.message_id ? { ...m, reactions: evt.reactions } : m,
          ),
        );
      }

      if (evt.type === "conversation:read" && evt.reader_id !== user.user_id) {
        // Peer read the thread — flip local read=true on every message I sent
        // so the "seen" indicator can render under my last message. The
        // read_at timestamp is propagated for a "seen 2:14 PM" line.
        const readAt = evt.read_at || new Date().toISOString();
        setMessages((prev) =>
          prev.map((m) =>
            m.sender_id === user.user_id && !m.read
              ? { ...m, read: true, read_at: readAt }
              : m,
          ),
        );
      }

      if (
        evt.type === "message:typing" &&
        evt.user_id !== user.user_id
      ) {
        setPeerTyping(Boolean(evt.typing));
        // Auto-expire stale `typing=true` after 5s of no follow-up event.
        clearTimeout(peerTypingTimerRef.current);
        if (evt.typing) {
          peerTypingTimerRef.current = setTimeout(
            () => setPeerTyping(false),
            5000,
          );
        }
      }
    });
  }, [subscribe, conversationId, user, conv, push]);

  // Toggle a reaction on a message. Optimistic — the WS event will confirm
  // (or roll back via a refetch if the server rejected the request).
  const toggleReaction = async (msg, emoji) => {
    setMessages((prev) =>
      prev.map((m) => {
        if (m.id !== msg.id) return m;
        const rx = { ...(m.reactions || {}) };
        const list = [...(rx[emoji] || [])];
        const i = list.indexOf(user.user_id);
        if (i >= 0) list.splice(i, 1);
        else list.push(user.user_id);
        if (list.length) rx[emoji] = list;
        else delete rx[emoji];
        return { ...m, reactions: rx };
      }),
    );
    try {
      await api.post(`/messages/${msg.id}/reactions/toggle`, { emoji });
    } catch (e) {
      console.warn("reaction toggle failed", e);
      // Refetch to un-do the optimistic change.
      loadMessages();
    }
  };

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight });
  }, [messages.length]);

  // Free the object URL when we're done previewing to avoid leaking blobs.
  useEffect(() => {
    return () => {
      if (mediaPreview && mediaPreview.startsWith("blob:")) {
        URL.revokeObjectURL(mediaPreview);
      }
    };
  }, [mediaPreview]);

  // Clean up outgoing/incoming typing timers on unmount so we don't fire
  // stale setState on an unmounted component.
  useEffect(() => {
    return () => {
      clearTimeout(typingTimerRef.current);
      clearTimeout(peerTypingTimerRef.current);
    };
  }, []);

  // Which of MY messages should show the "seen" indicator? Only the LAST
  // read one — earlier read messages are implied. Empty when the peer
  // hasn't read anything yet.
  const lastSeenMineId = useMemo(() => {
    for (let i = messages.length - 1; i >= 0; i--) {
      const m = messages[i];
      if (m.sender_id === user?.user_id && m.read) return m.id;
    }
    return null;
  }, [messages, user]);

  if (!user) {
    return (
      <div className="md:ml-60 min-h-screen flex items-center justify-center px-6 text-center">
        <button
          data-testid="conv-go-login-btn"
          onClick={() => navigate("/login")}
          className="bg-[#39FF14] text-black font-bold uppercase tracking-wider px-6 py-3"
        >
          Sign in
        </button>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="md:ml-60 min-h-screen flex items-center justify-center">
        <Loader2 className="h-8 w-8 text-[#39FF14] animate-spin" />
      </div>
    );
  }

  const submit = async (e) => {
    e.preventDefault();
    cancelTyping();
    // Media upload path — always fires as its OWN message so a caption +
    // media can arrive together without racing the text-only submit call.
    if (mediaFile) {
      if (sending) return;
      setSending(true);
      setUploadPct(0);
      const captionDraft = text;
      const local = { ...mediaFile };
      const preview = mediaPreview;
      setText("");
      setMediaFile(null);
      setMediaPreview(null);
      try {
        const form = new FormData();
        form.append("file", local.file);
        if (captionDraft.trim()) form.append("caption", captionDraft.trim());
        // Voice notes ship a duration so the recipient's bubble can show
        // a pill like "0:12" without decoding the whole blob.
        if (local.kind === "audio" && local.duration_sec) {
          form.append("duration_sec", String(local.duration_sec));
        }
        // Optional pre-computed waveform (40 floats in [0..1]). Server
        // silently drops malformed inputs so a rare compute failure never
        // blocks the send.
        if (local.kind === "audio" && Array.isArray(local.waveform) && local.waveform.length) {
          form.append("waveform", JSON.stringify(local.waveform));
        }
        const r = await api.post(
          `/messages/conversations/${conversationId}/media`,
          form,
          {
            
            onUploadProgress: (ev) => {
              if (!ev.total) return;
              setUploadPct(Math.round((ev.loaded * 100) / ev.total));
            },
          },
        );
        setMessages((prev) => [...prev, r.data]);
      } catch (err) {
        const detail = err?.response?.data?.detail || "Upload failed";
        toast.error(detail);
        // Restore the picker state so user doesn't lose the file.
        setMediaFile(local);
        setMediaPreview(preview);
        setText(captionDraft);
      } finally {
        setSending(false);
        setUploadPct(0);
      }
      return;
    }

    if ((!text.trim() && coinAmount <= 0) || sending) return;
    setSending(true);
    const draft = text;
    const coins = coinAmount;
    setText("");
    setCoinAmount(0);
    setCustomAmount("");
    setShowPicker(false);
    try {
      const r = await api.post(
        `/messages/conversations/${conversationId}/messages`,
        {
          text: draft,
          coins_amount: coins || undefined,
        },
      );
      setMessages((prev) => [...prev, r.data]);
      // Optimistic animation for the sender.
      if (coins > 0 && r.data?.coins_amount) {
        push({
          id: r.data.id,
          coins_amount: r.data.coins_amount,
          gift_tier: r.data.gift_tier || tierForCoins(r.data.coins_amount),
          text: r.data.text,
          tipper: { name: user.name, username: user.username, picture: user.picture },
        });
      }
    } catch (err) {
      const detail = err?.response?.data?.detail || "Failed to send";
      toast.error(detail);
      setText(draft);
      setCoinAmount(coins);
    } finally {
      setSending(false);
    }
  };

  const pickFile = () => fileInputRef.current?.click();

  const onFilePicked = (e) => {
    const f = e.target.files?.[0];
    // Reset the input immediately so the SAME file can be re-picked later.
    e.target.value = "";
    if (!f) return;
    const isImage = f.type.startsWith("image/");
    const isVideo = f.type.startsWith("video/");
    if (!isImage && !isVideo) {
      toast.error("Only images or videos are allowed");
      return;
    }
    const cap = isVideo ? VIDEO_MAX_BYTES : IMAGE_MAX_BYTES;
    if (f.size > cap) {
      const mb = Math.round(cap / (1024 * 1024));
      toast.error(`${isVideo ? "Video" : "Image"} too large (max ${mb}MB)`);
      return;
    }
    // Object URL lets us render a client-side preview without a round-trip.
    const objectUrl = URL.createObjectURL(f);
    setMediaFile({ file: f, kind: isVideo ? "video" : "image" });
    setMediaPreview(objectUrl);
  };

  // Free the object URL when we're done previewing to avoid leaking blobs.
  // (Also declared before the early returns above — this one is the runtime
  // pair for updated `mediaPreview` values.)

  const clearMedia = () => {
    setMediaFile(null);
    setMediaPreview(null);
  };

  // -----------------------------------------------------------------
  // Voice-note recording
  // -----------------------------------------------------------------
  // Pick the first MIME type the browser supports. Chrome/Firefox record
  // webm/opus natively; Safari lands on mp4/aac. Both are playable by the
  // recipient's <audio> element without server-side transcoding.
  const _pickAudioMime = () => {
    if (typeof window === "undefined" || !window.MediaRecorder) return "";
    const candidates = [
      "audio/webm;codecs=opus",
      "audio/webm",
      "audio/mp4;codecs=mp4a.40.2",
      "audio/mp4",
      "audio/ogg;codecs=opus",
    ];
    for (const t of candidates) {
      if (window.MediaRecorder.isTypeSupported(t)) return t;
    }
    return "";
  };

  const _stopRecordTimer = () => {
    if (recordTimerRef.current) {
      clearInterval(recordTimerRef.current);
      recordTimerRef.current = null;
    }
  };

  // Stop the rAF loop and release the AudioContext + Analyser used by the
  // live-recording waveform. Safe to call from any state.
  const _stopLiveViz = () => {
    if (liveRafRef.current) {
      cancelAnimationFrame(liveRafRef.current);
      liveRafRef.current = null;
    }
    if (liveAudioCtxRef.current) {
      try {
        liveAudioCtxRef.current.close();
      } catch {
        /* already closed */
      }
      liveAudioCtxRef.current = null;
    }
    liveAnalyserRef.current = null;
  };

  // Fully tear down the recorder + mic stream + live-viz graph. Safe to
  // call from any state.
  const _teardownRecorder = () => {
    _stopRecordTimer();
    _stopLiveViz();
    try {
      if (mediaRecorderRef.current && mediaRecorderRef.current.state !== "inactive") {
        mediaRecorderRef.current.stop();
      }
    } catch {
      /* already stopped */
    }
    mediaRecorderRef.current = null;
    if (audioStreamRef.current) {
      try {
        audioStreamRef.current.getTracks().forEach((t) => t.stop());
      } catch {
        /* stream already released */
      }
      audioStreamRef.current = null;
    }
  };

  const startRecording = async () => {
    if (recording || sending || mediaFile) return;
    if (!navigator.mediaDevices?.getUserMedia || !window.MediaRecorder) {
      toast.error("Your browser doesn't support voice recording");
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      audioStreamRef.current = stream;
      const mime = _pickAudioMime();
      const rec = mime
        ? new MediaRecorder(stream, { mimeType: mime })
        : new MediaRecorder(stream);
      audioChunksRef.current = [];
      rec.ondataavailable = (e) => {
        if (e.data && e.data.size > 0) audioChunksRef.current.push(e.data);
      };
      rec.onstop = () => {
        const blob = new Blob(audioChunksRef.current, {
          type: rec.mimeType || "audio/webm",
        });
        const durationSec = Math.max(
          0.1,
          (Date.now() - recordStartAtRef.current) / 1000,
        );
        if (blob.size > AUDIO_MAX_BYTES) {
          toast.error(`Voice note too long (max ${AUDIO_MAX_BYTES / (1024 * 1024)}MB)`);
        } else if (blob.size < 200) {
          toast.error("Recording too short");
        } else {
          // Stage the recorded blob as a File so the existing upload path
          // ("Send" button) can ship it without a separate code path.
          const extFromMime = (rec.mimeType || "audio/webm").split("/")[1].split(";")[0] || "webm";
          const file = new File(
            [blob],
            `voice-${Date.now()}.${extFromMime}`,
            { type: rec.mimeType || "audio/webm" },
          );
          const objectUrl = URL.createObjectURL(blob);
          // Kick off waveform computation asynchronously. We DON'T block the
          // preview UI on it — the user can send immediately and the
          // waveform will be attached if it's ready before the request
          // fires. Falls back to a flat bar row if not.
          setMediaFile({ file, kind: "audio", duration_sec: durationSec });
          setMediaPreview(objectUrl);
          computeWaveformFromBlob(blob).then((bars) => {
            if (bars && bars.length) {
              setMediaFile((prev) =>
                prev && prev.kind === "audio" ? { ...prev, waveform: bars } : prev,
              );
            }
          });
        }
        // Stream cleanup happens in _teardownRecorder from the callers.
      };
      recordStartAtRef.current = Date.now();
      setRecordingSec(0);
      setLiveBars(new Array(WAVEFORM_BARS).fill(0));

      // ---- Live waveform: sample mic frequency data every animation
      // frame and pool it into WAVEFORM_BARS values. fftSize=128 gives
      // 64 usable frequency bins — comfortably more than our bar count
      // so we can compress. Using getByteTimeDomainData would show the
      // literal waveform; freq data reads more like a WhatsApp meter
      // (bars react to volume + timbre, feel "alive").
      try {
        const Ctx = window.AudioContext || window.webkitAudioContext;
        if (Ctx) {
          const audioCtx = new Ctx();
          const source = audioCtx.createMediaStreamSource(stream);
          const analyser = audioCtx.createAnalyser();
          analyser.fftSize = 128;
          analyser.smoothingTimeConstant = 0.65;
          source.connect(analyser);
          liveAudioCtxRef.current = audioCtx;
          liveAnalyserRef.current = analyser;
          const binCount = analyser.frequencyBinCount; // 64
          const dataArr = new Uint8Array(binCount);
          const perBar = Math.max(1, Math.floor(binCount / WAVEFORM_BARS));
          const tick = () => {
            if (!liveAnalyserRef.current) return;
            liveAnalyserRef.current.getByteFrequencyData(dataArr);
            const next = new Array(WAVEFORM_BARS);
            for (let i = 0; i < WAVEFORM_BARS; i++) {
              let sum = 0;
              const start = i * perBar;
              const end = Math.min(binCount, start + perBar);
              for (let j = start; j < end; j++) sum += dataArr[j];
              next[i] = Math.min(1, sum / (perBar * 255));
            }
            setLiveBars(next);
            liveRafRef.current = requestAnimationFrame(tick);
          };
          liveRafRef.current = requestAnimationFrame(tick);
        }
      } catch (e) {
        // A failed AnalyserNode setup shouldn't block recording — the
        // strip just falls back to flat bars.
        console.warn("live waveform init failed", e);
      }

      rec.start();
      mediaRecorderRef.current = rec;
      setRecording(true);
      recordTimerRef.current = setInterval(() => {
        const secs = (Date.now() - recordStartAtRef.current) / 1000;
        setRecordingSec(secs);
        // Hard stop at the cap so users can't accidentally record 20min files.
        if (secs >= AUDIO_MAX_SECONDS) {
          stopRecording();
        }
      }, 200);
    } catch (e) {
      console.warn("mic permission denied", e);
      toast.error("Microphone permission denied");
      _teardownRecorder();
      setRecording(false);
    }
  };

  // Stop recording and stage the blob for the user to Send/cancel.
  const stopRecording = () => {
    if (!recording) return;
    setRecording(false);
    _teardownRecorder();
  };

  // Discard the in-progress recording without staging it.
  const cancelRecording = () => {
    setRecording(false);
    audioChunksRef.current = [];
    _teardownRecorder();
    setRecordingSec(0);
  };

  // Emit typing=true (throttled to once every 3s) and schedule a typing=false
  // ~3s after the last keystroke. Called from the input's onChange.
  const signalTyping = () => {
    const now = Date.now();
    if (now - typingLastSentRef.current > 3000) {
      typingLastSentRef.current = now;
      api
        .post(`/messages/conversations/${conversationId}/typing`, { typing: true })
        .catch(() => {});
    }
    clearTimeout(typingTimerRef.current);
    typingTimerRef.current = setTimeout(() => {
      typingLastSentRef.current = 0;
      api
        .post(`/messages/conversations/${conversationId}/typing`, { typing: false })
        .catch(() => {});
    }, 3000);
  };

  const cancelTyping = () => {
    clearTimeout(typingTimerRef.current);
    if (typingLastSentRef.current > 0) {
      typingLastSentRef.current = 0;
      api
        .post(`/messages/conversations/${conversationId}/typing`, { typing: false })
        .catch(() => {});
    }
  };

  const pickerCfg = coinAmount > 0 ? TIERS[tierForCoins(coinAmount) || 10] : null;

  // lastSeenMineId is computed above the early returns.
  return (
    <div className="md:ml-60 min-h-screen flex flex-col h-screen">
      {/* Gift animation overlay */}
      <GiftQueue queue={queue} onFinish={clear} />

      {/* header */}
      <div className="border-b border-white/10 bg-[#050505] px-4 sm:px-6 py-4 flex items-center gap-4 sticky top-0 z-20">
        <button
          onClick={() => navigate("/messages")}
          data-testid="conv-back-btn"
          className="h-9 w-9 border border-white/10 hover:border-white/30 flex items-center justify-center"
        >
          <ArrowLeft className="h-4 w-4" />
        </button>
        {conv?.peer && (
          <Link
            to={`/u/${conv.peer.username}`}
            data-testid="conv-peer-link"
            className="flex items-center gap-3 flex-1 min-w-0 hover:opacity-80"
          >
            {conv.peer.picture ? (
              <img
                src={picUrl(conv.peer.picture)}
                alt=""
                className="h-10 w-10 object-cover border border-[#39FF14]/40"
              />
            ) : (
              <div className="h-10 w-10 bg-[#FF007F]/20 flex items-center justify-center font-display font-bold text-[#FF007F]">
                {conv.peer.name?.[0]?.toUpperCase()}
              </div>
            )}
            <div className="min-w-0">
              <p className="font-semibold text-white truncate">
                {conv.peer.name}
              </p>
              <p className="font-mono text-[10px] text-white/40 truncate">
                @{conv.peer.username}
              </p>
            </div>
          </Link>
        )}
      </div>

      {/* messages */}
      <div
        ref={scrollRef}
        className="flex-1 overflow-y-auto px-4 sm:px-6 py-6 scrollbar-hide bg-[#050505]"
        data-testid="conv-messages"
      >
        {messages.length === 0 ? (
          <div className="h-full flex flex-col items-center justify-center text-center text-white/40">
            <p className="font-display text-lg">Channel open</p>
            <p className="font-mono text-xs mt-2 uppercase tracking-widest">
              say something to {conv?.peer?.name?.split(" ")[0]}
            </p>
          </div>
        ) : (
          <ul className="space-y-2 max-w-2xl mx-auto">
            {messages.map((m, idx) => (
              <MessageRow
                key={m.id}
                message={m}
                prev={messages[idx - 1]}
                mine={m.sender_id === user.user_id}
                showSeen={m.id === lastSeenMineId}
                onReplay={push}
                onToggleReaction={toggleReaction}
                peer={conv?.peer}
                me={user}
              />
            ))}
            {peerTyping && (
              <li
                data-testid="conv-typing-indicator"
                className="flex items-start"
              >
                <div className="bg-white/5 border border-white/10 text-white px-4 py-2.5 flex items-center gap-1.5">
                  <span className="typing-dot" />
                  <span className="typing-dot" style={{ animationDelay: "0.15s" }} />
                  <span className="typing-dot" style={{ animationDelay: "0.3s" }} />
                </div>
              </li>
            )}
          </ul>
        )}
      </div>

      <style>{`
        .typing-dot {
          display: inline-block;
          width: 6px;
          height: 6px;
          background: #00FFFF;
          border-radius: 999px;
          opacity: 0.5;
          animation: typingBounce 1s infinite ease-in-out;
        }
        @keyframes typingBounce {
          0%, 60%, 100% { transform: translateY(0); opacity: 0.5; }
          30% { transform: translateY(-3px); opacity: 1; }
        }
      `}</style>

      {/* coin picker */}
      {showPicker && (
        <CoinPicker
          coinAmount={coinAmount}
          setCoinAmount={setCoinAmount}
          customAmount={customAmount}
          setCustomAmount={setCustomAmount}
          creatorPct={creatorPct}
          pickerCfg={pickerCfg}
        />
      )}

      {/* voice-note recording strip — replaces the composer's normal look
          while the mic is live. Live 40-bar waveform driven by an
          AnalyserNode dances with your voice. Timer + STOP + CANCEL. */}
      {recording && (
        <div
          data-testid="conv-recording-strip"
          className="border-t border-white/10 p-3 flex items-center gap-3 bg-black/70"
        >
          <span
            data-testid="conv-recording-dot"
            className="h-2 w-2 rounded-full bg-[#FF007F] shadow-[0_0_10px_rgba(255,0,127,0.9)] animate-pulse shrink-0"
          />
          <div
            data-testid="conv-recording-bars"
            className="flex items-center gap-[2px] h-8 flex-1 min-w-[100px]"
          >
            {liveBars.map((v, i) => (
              <span
                key={i}
                className="flex-1 h-full flex items-center justify-center"
              >
                <span
                  className="w-full rounded-[1px]"
                  style={{
                    height: `${Math.max(10, Math.round(v * 100))}%`,
                    background: "#FF007F",
                    boxShadow: v > 0.15 ? "0 0 5px rgba(255,0,127,0.55)" : undefined,
                    opacity: 0.35 + Math.min(0.65, v * 1.1),
                    transition: "height 60ms linear, opacity 60ms linear",
                  }}
                />
              </span>
            ))}
          </div>
          <p
            data-testid="conv-recording-timer"
            className="font-display text-lg text-white leading-tight tabular-nums shrink-0"
          >
            {formatDurationSec(recordingSec)}
            <span className="font-mono text-[10px] text-white/40 ml-1.5">
              / {formatDurationSec(AUDIO_MAX_SECONDS)}
            </span>
          </p>
          <button
            type="button"
            onClick={cancelRecording}
            data-testid="conv-recording-cancel"
            title="Discard recording"
            className="h-10 w-10 border border-white/10 hover:border-[#FF3B30] text-white/60 hover:text-[#FF3B30] flex items-center justify-center transition-all shrink-0"
          >
            <Trash2 className="h-4 w-4" />
          </button>
          <button
            type="button"
            onClick={stopRecording}
            data-testid="conv-recording-stop"
            title="Finish recording"
            className="h-10 px-4 border border-[#FF007F] bg-[#FF007F] text-black font-mono text-[11px] uppercase tracking-widest font-bold hover:shadow-[0_0_18px_rgba(255,0,127,0.7)] transition-all shrink-0"
          >
            stop
          </button>
        </div>
      )}

      {/* media preview strip — shows the staged image/video before send.
          Progress bar overlays the thumbnail while the upload is in flight. */}
      {mediaFile && mediaPreview && (
        <div
          data-testid="conv-media-preview"
          className="border-t border-white/10 p-3 flex items-center gap-3 bg-black/60"
        >
          <div className="relative shrink-0 border border-white/10 bg-black">
            {mediaFile.kind === "video" ? (
              <video
                src={mediaPreview}
                className="h-16 w-16 object-cover"
                muted
              />
            ) : mediaFile.kind === "audio" ? (
              <div className="h-16 w-16 bg-[#FF007F]/10 flex items-center justify-center text-[#FF007F]">
                <Mic className="h-6 w-6" strokeWidth={1.8} />
              </div>
            ) : (
              <img
                src={mediaPreview}
                alt="preview"
                className="h-16 w-16 object-cover"
              />
            )}
            {sending && uploadPct > 0 && (
              <div className="absolute inset-x-0 bottom-0 h-1 bg-black/70">
                <div
                  className="h-full bg-[#39FF14] transition-all"
                  style={{ width: `${uploadPct}%` }}
                />
              </div>
            )}
          </div>
          <div className="min-w-0 flex-1">
            <p className="font-mono text-[10px] uppercase tracking-widest text-white/60 truncate flex items-center gap-1.5">
              {mediaFile.kind === "video" ? (
                <Film className="h-3 w-3" />
              ) : mediaFile.kind === "audio" ? (
                <Mic className="h-3 w-3" />
              ) : (
                <ImageIcon className="h-3 w-3" />
              )}
              {mediaFile.kind === "audio"
                ? `voice note · ${formatDurationSec(mediaFile.duration_sec)}`
                : mediaFile.file.name}
            </p>
            {mediaFile.kind === "audio" ? (
              <audio
                src={mediaPreview}
                controls
                preload="metadata"
                data-testid="conv-audio-preview"
                className="mt-1 h-8 w-full max-w-[240px]"
              />
            ) : (
              <p className="font-mono text-[10px] text-white/40">
                {sending
                  ? `uploading… ${uploadPct}%`
                  : `${(mediaFile.file.size / (1024 * 1024)).toFixed(1)} MB · tap send to share`}
              </p>
            )}
          </div>
          <button
            type="button"
            onClick={clearMedia}
            disabled={sending}
            data-testid="conv-media-clear"
            title="Remove attachment"
            className="h-8 w-8 border border-white/10 hover:border-[#FF3B30] text-white/60 hover:text-[#FF3B30] flex items-center justify-center disabled:opacity-40 transition-all"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        </div>
      )}

      {/* composer */}
      <form
        onSubmit={submit}
        className="border-t border-white/10 p-4 flex items-center gap-2 bg-[#050505] mb-14 md:mb-0"
        data-testid="conv-composer"
      >
        {/* Hidden file input driven by the paperclip button below. */}
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*,video/*"
          onChange={onFilePicked}
          className="hidden"
          data-testid="conv-file-input"
        />
        <button
          type="button"
          onClick={pickFile}
          disabled={sending || Boolean(mediaFile) || recording}
          data-testid="conv-attach-btn"
          title="Attach image or video"
          className="flex items-center justify-center h-[46px] w-11 border border-white/10 hover:border-[#00FFFF] text-white/40 hover:text-[#00FFFF] disabled:opacity-30 disabled:hover:border-white/10 disabled:hover:text-white/40 transition-all"
        >
          <Paperclip className="h-4 w-4" strokeWidth={1.8} />
        </button>
        <button
          type="button"
          onClick={startRecording}
          disabled={sending || Boolean(mediaFile) || recording}
          data-testid="conv-mic-btn"
          title="Record a voice note"
          className="flex items-center justify-center h-[46px] w-11 border border-white/10 hover:border-[#FF007F] text-white/40 hover:text-[#FF007F] disabled:opacity-30 disabled:hover:border-white/10 disabled:hover:text-white/40 transition-all"
        >
          <Mic className="h-4 w-4" strokeWidth={1.8} />
        </button>
        <button
          type="button"
          onClick={() => setShowPicker((v) => !v)}
          data-testid="conv-coin-toggle"
          title="Attach coins to your message"
          disabled={Boolean(mediaFile)}
          className={`flex items-center justify-center h-[46px] w-11 border transition-all disabled:opacity-30 ${
            coinAmount > 0
              ? "text-black font-bold"
              : showPicker
              ? "border-[#FF007F] text-[#FF007F]"
              : "border-white/10 text-white/40 hover:text-[#FF007F] hover:border-[#FF007F]/50"
          }`}
          style={
            coinAmount > 0 && pickerCfg
              ? { background: pickerCfg.color, borderColor: pickerCfg.color }
              : undefined
          }
        >
          {coinAmount > 0 ? (
            <span className="font-mono text-[10px] leading-none">
              +{coinAmount >= 1000 ? `${(coinAmount / 1000).toFixed(coinAmount % 1000 === 0 ? 0 : 1)}K` : coinAmount}
            </span>
          ) : (
            <Coins className="h-4 w-4" strokeWidth={1.8} />
          )}
        </button>
        <input
          value={text}
          onChange={(e) => {
            setText(e.target.value);
            if (e.target.value.length > 0) signalTyping();
            else cancelTyping();
          }}
          onBlur={cancelTyping}
          placeholder={
            mediaFile
              ? "add a caption (optional)…"
              : coinAmount > 0
              ? "add a note (optional)…"
              : "type a message…"
          }
          data-testid="conv-input"
          maxLength={2000}
          className="flex-1 bg-white/5 border border-white/10 text-white placeholder:text-white/30 px-4 py-3 focus:outline-none focus:border-[#00FFFF] focus:ring-1 focus:ring-[#00FFFF]/40 font-mono text-sm"
        />
        <button
          type="submit"
          disabled={sending || (!text.trim() && coinAmount <= 0 && !mediaFile)}
          data-testid="conv-send-btn"
          className="bg-[#39FF14] text-black px-5 py-3 disabled:opacity-30 hover:shadow-[0_0_15px_rgba(57,255,20,0.5)] transition-all"
        >
          {sending ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <Send className="h-4 w-4" />
          )}
        </button>
      </form>
    </div>
  );
}

// -------------------------------------------------------------------------
// One message bubble. If `coins_amount > 0`, render the gift-styled variant
// (glow, tier label, replay button); otherwise plain text bubble.
// -------------------------------------------------------------------------
function MessageRow({ message: m, prev, mine, showSeen, onReplay, onToggleReaction, peer, me }) {
  const showTime =
    !prev || new Date(m.created_at) - new Date(prev.created_at) > 5 * 60 * 1000;

  const isGift = m.coins_amount && m.coins_amount > 0;
  const isMedia = Boolean(m.media_url);
  const tier = useMemo(
    () => (isGift ? m.gift_tier || tierForCoins(m.coins_amount) || 10 : null),
    [isGift, m.gift_tier, m.coins_amount],
  );
  const cfg = tier ? TIERS[tier] : null;

  // Reaction picker visibility — a small popover shown on long-press (mobile)
  // or hover-with-plus (desktop). Timer ref lets us cancel a fired long-press
  // if the user just tapped without holding.
  const [showReactions, setShowReactions] = useState(false);
  const longPressTimer = useRef(null);

  const startLongPress = () => {
    clearTimeout(longPressTimer.current);
    longPressTimer.current = setTimeout(() => setShowReactions(true), 380);
  };
  const cancelLongPress = () => {
    clearTimeout(longPressTimer.current);
  };
  useEffect(() => () => clearTimeout(longPressTimer.current), []);

  const replay = () => {
    if (!isGift) return;
    onReplay({
      id: m.id,
      coins_amount: m.coins_amount,
      gift_tier: tier,
      text: m.text,
      tipper: mine
        ? { name: me?.name, username: me?.username, picture: me?.picture }
        : peer || { name: "someone", username: "someone" },
    });
  };

  // Reactions blob — a dict of { "🔥": ["user_id_a", "user_id_b"], ... }.
  // Filter empty lists defensively (backend prunes them but WS races
  // occasionally leak an empty array).
  const reactionEntries = Object.entries(m.reactions || {}).filter(
    ([, users]) => Array.isArray(users) && users.length > 0,
  );

  const handleReactClick = (emoji) => {
    setShowReactions(false);
    onToggleReaction(m, emoji);
  };

  return (
    <li
      data-testid={`message-${m.id}`}
      className={`flex flex-col ${mine ? "items-end" : "items-start"}`}
    >
      {showTime && (
        <p className="font-mono text-[10px] uppercase tracking-widest text-white/30 my-2 self-center">
          {timeLabel(m.created_at)}
        </p>
      )}

      <div
        className={`group relative flex ${mine ? "flex-row-reverse" : "flex-row"} items-end gap-1.5 max-w-[85%]`}
        onTouchStart={startLongPress}
        onTouchEnd={cancelLongPress}
        onTouchCancel={cancelLongPress}
        onTouchMove={cancelLongPress}
        onMouseDown={startLongPress}
        onMouseUp={cancelLongPress}
        onMouseLeave={cancelLongPress}
      >
        {/* Add-reaction affordance — visible on hover (desktop) or via
            long-press on mobile. The + button is redundant on mobile but
            gives a discoverable entry point on desktop. */}
        <button
          type="button"
          data-testid={`message-react-btn-${m.id}`}
          onClick={(e) => {
            e.stopPropagation();
            setShowReactions((v) => !v);
          }}
          className="opacity-0 group-hover:opacity-100 focus:opacity-100 text-white/40 hover:text-[#00FFFF] transition-all shrink-0 mb-1 text-xs font-bold w-6 h-6 flex items-center justify-center border border-white/10 hover:border-[#00FFFF]/50"
          title="Add reaction"
        >
          +
        </button>

        <div className="flex flex-col">
          {isMedia ? (
            <MediaBubble m={m} mine={mine} />
          ) : isGift && cfg ? (
            <div
              data-testid={`gift-message-${m.id}`}
              data-tier={tier}
              className="p-3 border transition-all"
              style={{
                borderColor: `${cfg.color}55`,
                background: `linear-gradient(90deg, ${cfg.color}18 0%, transparent 100%)`,
                boxShadow: `0 0 18px ${cfg.color}20`,
              }}
            >
              <div className="flex items-center gap-2 mb-2">
                <Sparkles
                  className="h-3.5 w-3.5"
                  style={{ color: cfg.color }}
                  strokeWidth={2}
                />
                <span
                  className="font-mono text-[10px] uppercase tracking-widest"
                  style={{ color: cfg.color }}
                >
                  // {cfg.label}
                </span>
                <span
                  className="font-display font-black text-sm"
                  style={{ color: cfg.color }}
                >
                  +{m.coins_amount.toLocaleString()} coins
                </span>
                <button
                  type="button"
                  onClick={replay}
                  data-testid={`gift-message-replay-${m.id}`}
                  title="Replay animation"
                  className="ml-auto font-mono text-[9px] uppercase tracking-widest text-white/40 hover:text-white/80 border border-white/10 hover:border-white/30 px-2 py-0.5 transition-colors"
                >
                  ▶ replay
                </button>
              </div>
              {m.text && (
                <p className="text-sm text-white break-words whitespace-pre-wrap">
                  {m.text}
                </p>
              )}
            </div>
          ) : (
            <div
              className={`px-4 py-2.5 text-sm break-words ${
                mine
                  ? "bg-[#39FF14] text-black"
                  : "bg-white/5 border border-white/10 text-white"
              }`}
            >
              {m.text}
            </div>
          )}

          {/* Reaction chips row — sits directly under the bubble on the
              same alignment side. Empty lists are filtered above so we
              only render active reactions. */}
          {reactionEntries.length > 0 && (
            <div
              data-testid={`reactions-${m.id}`}
              className={`flex flex-wrap gap-1 mt-1 ${mine ? "justify-end" : "justify-start"}`}
            >
              {reactionEntries.map(([emoji, users]) => {
                const mineReact = users.includes(me?.user_id);
                return (
                  <button
                    key={emoji}
                    type="button"
                    onClick={() => onToggleReaction(m, emoji)}
                    data-testid={`reaction-chip-${m.id}-${emoji}`}
                    className={`flex items-center gap-1 px-1.5 py-0.5 border text-xs transition-colors ${
                      mineReact
                        ? "bg-[#00FFFF]/20 border-[#00FFFF]/60 text-[#00FFFF]"
                        : "bg-white/5 border-white/10 text-white/70 hover:border-white/30"
                    }`}
                  >
                    <span>{emoji}</span>
                    <span className="font-mono text-[10px]">{users.length}</span>
                  </button>
                );
              })}
            </div>
          )}

          {/* "seen" indicator — only under the LAST of my own messages that
              the peer has read. Timestamp is the moment they opened the
              chat, per the conversation:read WS event. */}
          {mine && showSeen && m.read_at && (
            <p
              data-testid={`seen-indicator-${m.id}`}
              className="font-mono text-[9px] uppercase tracking-widest text-white/40 mt-1"
            >
              seen · {timeLabel(m.read_at)}
            </p>
          )}
        </div>

        {/* Popover picker — shown on long-press or when the + button is
            clicked. Positioned above the bubble so it doesn't cover it. */}
        {showReactions && (
          <div
            data-testid={`reaction-picker-${m.id}`}
            className={`absolute bottom-full mb-1 ${mine ? "right-0" : "left-0"} z-10 flex items-center gap-1 px-2 py-1 bg-black border border-white/20 shadow-[0_0_18px_rgba(0,255,255,0.25)]`}
            onMouseLeave={() => setShowReactions(false)}
          >
            {REACTIONS.map((emoji) => {
              const mineReact = (m.reactions?.[emoji] || []).includes(me?.user_id);
              return (
                <button
                  key={emoji}
                  type="button"
                  onClick={() => handleReactClick(emoji)}
                  data-testid={`reaction-pick-${m.id}-${emoji}`}
                  className={`text-lg transition-transform hover:scale-125 ${mineReact ? "opacity-100" : "opacity-90"}`}
                >
                  {emoji}
                </button>
              );
            })}
          </div>
        )}
      </div>
    </li>
  );
}

// -------------------------------------------------------------------------
// Media bubble — image or video attachment plus optional caption underneath.
// Both types are lazy-loaded and constrained to a max width so a single
// portrait clip doesn't blow out the message column on mobile.
// -------------------------------------------------------------------------
// -------------------------------------------------------------------------
// VoiceNotePlayer — WhatsApp/Instagram-style waveform bar chart with an
// integrated play/pause button. Uses a hidden `<audio>` element for
// actual decoding + timeupdate; the visible UI is pure divs. Bars that
// have been played are filled with the accent color; the rest sit at
// low opacity. Click any bar to seek. Falls back to flat bars if the
// server didn't ship a `audio_waveform` array.
// -------------------------------------------------------------------------
function VoiceNotePlayer({ url, waveform, durationSec, mine, testId }) {
  const audioRef = useRef(null);
  const containerRef = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [progress, setProgress] = useState(0); // 0..1
  const [currentSec, setCurrentSec] = useState(0);
  const bars = useMemo(() => {
    if (Array.isArray(waveform) && waveform.length) return waveform;
    // No waveform → generate a subtle pseudo-random row so the bar
    // container isn't visually empty. Deterministic from the URL so the
    // same message shows the same shape on every render.
    let seed = 0;
    for (let i = 0; i < (url || "").length; i++) seed = (seed * 31 + url.charCodeAt(i)) & 0xff;
    const rng = (n) => {
      seed = (seed * 9301 + 49297) & 0x7fffffff;
      return ((seed >> n) & 0xff) / 255;
    };
    return Array.from({ length: WAVEFORM_BARS }, (_, i) => 0.25 + 0.55 * rng(i % 8));
  }, [waveform, url]);

  const total = durationSec || audioRef.current?.duration || 0;
  const accent = mine ? "#FF007F" : "#00FFFF";

  const toggle = () => {
    const el = audioRef.current;
    if (!el) return;
    if (el.paused) {
      el.play().catch(() => {
        toast.error("Playback blocked — tap again");
      });
    } else {
      el.pause();
    }
  };

  const seekTo = (frac) => {
    const el = audioRef.current;
    if (!el || !isFinite(el.duration) || el.duration <= 0) return;
    el.currentTime = Math.max(0, Math.min(el.duration - 0.01, frac * el.duration));
    setProgress(frac);
  };

  const onBarClick = (idx) => seekTo((idx + 0.5) / bars.length);

  return (
    <div
      data-testid={testId}
      className={`max-w-[80%] px-3 py-2.5 flex items-center gap-3 border ${
        mine
          ? "bg-[#FF007F]/10 border-[#FF007F]/50"
          : "bg-white/5 border-white/10"
      }`}
    >
      <button
        type="button"
        onClick={toggle}
        data-testid={`${testId}-toggle`}
        title={playing ? "Pause" : "Play"}
        className="shrink-0 h-9 w-9 flex items-center justify-center border transition-all"
        style={{
          borderColor: accent,
          color: accent,
          background: playing ? `${accent}22` : "transparent",
        }}
      >
        {playing ? (
          <Pause className="h-4 w-4" fill={accent} strokeWidth={0} />
        ) : (
          <Play className="h-4 w-4 ml-[1px]" fill={accent} strokeWidth={0} />
        )}
      </button>
      <div
        ref={containerRef}
        data-testid={`${testId}-bars`}
        className="flex items-center gap-[2px] h-8 flex-1 min-w-[120px] max-w-[220px] cursor-pointer"
        onClick={(e) => {
          const rect = e.currentTarget.getBoundingClientRect();
          seekTo(Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width)));
        }}
      >
        {bars.map((v, i) => {
          const played = i / bars.length < progress;
          const heightPct = Math.max(12, Math.round(v * 100));
          return (
            <span
              key={i}
              onClick={(e) => {
                e.stopPropagation();
                onBarClick(i);
              }}
              className="flex-1 h-full flex items-center justify-center"
            >
              <span
                className="w-full rounded-[1px] transition-colors"
                style={{
                  height: `${heightPct}%`,
                  background: played ? accent : "rgba(255,255,255,0.28)",
                  boxShadow: played ? `0 0 4px ${accent}66` : undefined,
                }}
              />
            </span>
          );
        })}
      </div>
      <span
        data-testid={`${testId}-time`}
        className="font-mono text-[10px] uppercase tracking-widest shrink-0 tabular-nums"
        style={{ color: playing || progress > 0 ? accent : "rgba(255,255,255,0.5)" }}
      >
        {formatDurationSec(playing || progress > 0 ? currentSec : total)}
      </span>
      <audio
        ref={audioRef}
        src={url}
        preload="metadata"
        onPlay={() => setPlaying(true)}
        onPause={() => setPlaying(false)}
        onEnded={() => {
          setPlaying(false);
          setProgress(0);
          setCurrentSec(0);
        }}
        onTimeUpdate={(e) => {
          const el = e.currentTarget;
          if (!isFinite(el.duration) || el.duration <= 0) return;
          setCurrentSec(el.currentTime);
          setProgress(el.currentTime / el.duration);
        }}
        className="hidden"
      />
    </div>
  );
}

function MediaBubble({ m, mine }) {
  const url = mediaUrl(m);
  const isVideo = m.media_kind === "video";
  const isAudio = m.media_kind === "audio";
  if (isAudio) {
    return (
      <VoiceNotePlayer
        url={url}
        waveform={m.audio_waveform}
        durationSec={m.audio_duration_sec}
        mine={mine}
        testId={`media-message-${m.id}`}
      />
    );
  }
  return (
    <div
      data-testid={`media-message-${m.id}`}
      className={`max-w-[80%] overflow-hidden border ${
        mine ? "border-[#39FF14]/40" : "border-white/10"
      }`}
    >
      {isVideo ? (
        <video
          src={url}
          controls
          preload="metadata"
          playsInline
          className="block w-full max-h-[70vh] bg-black"
          data-testid={`media-video-${m.id}`}
        />
      ) : (
        <a href={url} target="_blank" rel="noreferrer" data-testid={`media-image-link-${m.id}`}>
          <img
            src={url}
            alt={m.text || "shared image"}
            loading="lazy"
            className="block w-full max-h-[70vh] object-contain bg-black"
            data-testid={`media-image-${m.id}`}
          />
        </a>
      )}
      {m.text && (
        <div
          className={`px-3 py-2 text-sm break-words ${
            mine ? "bg-[#39FF14] text-black" : "bg-white/5 text-white"
          }`}
        >
          {m.text}
        </div>
      )}
    </div>
  );
}

// -------------------------------------------------------------------------
// Coin picker panel — mirrors the CommentDrawer picker so the two gift
// surfaces feel identical to users.
// -------------------------------------------------------------------------
function CoinPicker({
  coinAmount,
  setCoinAmount,
  customAmount,
  setCustomAmount,
  creatorPct,
  pickerCfg,
}) {
  return (
    <div
      data-testid="conv-coin-picker"
      className="border-t border-white/10 p-4 bg-black/60"
    >
      <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#FF007F] mb-2">
        // attach.coins
      </p>
      <div className="flex flex-wrap gap-2 mb-3">
        {COIN_PRESETS.map((n) => {
          const cfg = TIERS[n];
          const active = coinAmount === n && !customAmount;
          return (
            <button
              key={n}
              type="button"
              data-testid={`conv-coin-preset-${n}`}
              onClick={() => {
                setCoinAmount(n);
                setCustomAmount("");
              }}
              className={`px-3 py-1.5 font-mono text-[11px] uppercase tracking-widest border transition-all ${
                active
                  ? "text-black font-bold"
                  : "text-white/70 hover:text-white"
              }`}
              style={{
                background: active ? cfg.color : "transparent",
                borderColor: active ? cfg.color : cfg.color + "55",
              }}
              title={`${cfg.label} · $${(n * 0.01).toFixed(2)} sent · $${((n * creatorPct / 100) * 0.01).toFixed(2)} to recipient`}
            >
              {n}
            </button>
          );
        })}
      </div>
      <div className="flex items-center gap-2">
        <input
          type="number"
          min="1"
          max="10000"
          inputMode="numeric"
          value={customAmount}
          data-testid="conv-coin-custom"
          onChange={(e) => {
            const v = e.target.value.replace(/[^0-9]/g, "");
            setCustomAmount(v);
            const n = parseInt(v || "0", 10);
            setCoinAmount(n > 0 ? n : 0);
          }}
          placeholder="custom amount"
          className="flex-1 bg-white/5 border border-white/10 focus:border-[#FF007F] focus:ring-1 focus:ring-[#FF007F]/40 outline-none px-3 py-2 font-mono text-xs text-white placeholder:text-white/30"
        />
        <button
          type="button"
          onClick={() => {
            setCoinAmount(0);
            setCustomAmount("");
          }}
          data-testid="conv-coin-clear"
          className="font-mono text-[10px] uppercase tracking-widest text-white/40 hover:text-white/80 px-2"
        >
          clear
        </button>
      </div>
      {coinAmount > 0 && pickerCfg && (
        <div
          data-testid="conv-coin-preview"
          className="mt-3 flex flex-col gap-0.5 font-mono text-[10px] uppercase tracking-widest"
          style={{ color: pickerCfg.color }}
        >
          <div className="flex items-center gap-2">
            <Sparkles className="h-3 w-3" />
            {coinAmount.toLocaleString()} coins · {pickerCfg.label} tier · ${(coinAmount * 0.01).toFixed(2)}
          </div>
          <div className="text-white/50 ml-5">
            → {Math.floor((coinAmount * creatorPct) / 100).toLocaleString()} coins
            (${((Math.floor((coinAmount * creatorPct) / 100)) * 0.01).toFixed(2)})
            to recipient · {creatorPct}/{100 - creatorPct} split
          </div>
        </div>
      )}
    </div>
  );
}
